import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Divider,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

// Per-card accent palette (cycled) + per-claim-type colors for a lively board.
const CARD_COLORS = ['#76B900', '#2196F3', '#9C27B0', '#FF9800', '#00BCD4'];
const TYPE_COLORS = { text: '#2196F3', boolean: '#76B900', date: '#FF9800', number: '#9C27B0', url: '#00BCD4' };
const typeColor = (t) => TYPE_COLORS[t] || '#78909C';

// Component for displaying credential types and their claims
const CredentialTypes = ({ credentialTypes, onEdit, onDelete }) => {
  // State for expanded accordions
  const [expanded, setExpanded] = useState(null);
  
  // Handle accordion expansion
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : null);
  };
  
  // If no credential types, show empty state
  if (!credentialTypes || credentialTypes.length === 0) {
    return (
      <Card variant="outlined" sx={{ textAlign: 'center', py: 5 }}>
        <CardContent>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Credential Types Yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first credential type to get started.
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => onEdit(null)}
          >
            Create Credential Type
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Box>
      {credentialTypes.map((type, ti) => {
        const accent = CARD_COLORS[ti % CARD_COLORS.length];
        return (
        <Card key={type.id} variant="outlined" sx={{ mb: 3, borderLeft: `5px solid ${accent}`, overflow: 'hidden' }}>
          <CardContent sx={{ pb: 1, background: `linear-gradient(135deg, ${accent}14, transparent 60%)` }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs>
                <Typography variant="h6" fontFamily="Cousine, monospace" sx={{ color: accent, fontWeight: 700 }}>
                  {type.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {type.description}
                </Typography>
              </Grid>
              <Grid item>
                <Chip
                  label={`${type.claims.length} ${type.claims.length === 1 ? 'Claim' : 'Claims'}`}
                  size="small"
                  sx={{ bgcolor: `${accent}22`, color: accent, border: `1px solid ${accent}66`, fontWeight: 700 }}
                />
              </Grid>
              <Grid item>
                <Tooltip title="Edit">
                  <IconButton color="primary" size="small" onClick={() => onEdit(type)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton color="error" size="small" onClick={() => onDelete(type.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </CardContent>
          
          <Divider />
          
          <Accordion 
            expanded={expanded === `panel-${type.id}`} 
            onChange={handleAccordionChange(`panel-${type.id}`)}
            elevation={0}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ px: 2, py: 0 }}
            >
              <Typography>View Claims</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><Typography variant="subtitle2">Claim</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2">Label</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2">Type</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2">Required</Typography></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {type.claims.map((claim, idx) => (
                      <TableRow key={idx}>
                        <TableCell><code style={{ color: accent }}>{claim.key || claim.name}</code></TableCell>
                        <TableCell>{claim.label || claim.description || ''}</TableCell>
                        <TableCell>
                          <Chip label={claim.type || 'text'} size="small"
                            sx={{ height: 20, fontSize: '0.7rem', textTransform: 'lowercase',
                              bgcolor: `${typeColor(claim.type || 'text')}22`, color: typeColor(claim.type || 'text'),
                              border: `1px solid ${typeColor(claim.type || 'text')}55`, fontWeight: 700 }} />
                        </TableCell>
                        <TableCell>
                          {claim.required
                            ? <Chip label="Required" size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'rgba(118,185,0,0.15)', color: '#5c8f00', fontWeight: 700 }} />
                            : <Chip label="Optional" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </Card>
        );
      })}
    </Box>
  );
};

export default CredentialTypes;